import { Link } from "react-router-dom";
import { Crown, Gem, Landmark, ChevronRight } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import MandalaDivider from "@/components/MandalaDivider";

function SectionTitle({ icon: Icon, eyebrow, title }) {
  return (
    <div className="reveal text-center">
      <div className="eyebrow flex items-center justify-center gap-2">
        <Icon size={13} className="text-[#D4AF37]" /> {eyebrow}
      </div>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#2A2118] leading-[1.1]">{title}</h2>
      <div className="mt-5 flex justify-center"><MandalaDivider /></div>
    </div>
  );
}

export default function SamsthanaIdols() {
  const { t } = useLang();
  const d = t.idols;
  return (
    <div data-testid="idols-page">
      <PageHeader eyebrow={d.eyebrow} title={d.title} sub={d.sub} />

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">

          <div>
            <SectionTitle icon={Crown} eyebrow={d.s1Eyebrow} title={d.s1Title} />
            <div className="mt-12 grid md:grid-cols-2 gap-8">
              <article data-testid="idol-digvijaya-rama" className="card-soft reveal overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-[#F4EBDB]">
                  <img src={ASSETS.deity3} alt={d.d1Title} className="w-full h-full object-cover" />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-[#7B1818] leading-snug">{d.d1Title}</h3>
                  <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-[1.8]">{d.d1Text}</p>
                </div>
              </article>
              <article data-testid="idol-kodandarama" className="card-soft reveal overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-[#F4EBDB]">
                  <img src={ASSETS.bannerDeitiesGarlanded} alt={d.d2Title} className="w-full h-full object-cover" />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-[#7B1818] leading-snug">{d.d2Title}</h3>
                  <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-[1.8]">{d.d2Text}</p>
                </div>
              </article>
            </div>
          </div>

          <div>
            <SectionTitle icon={Gem} eyebrow={d.s2Eyebrow} title={d.s2Title} />
            <div className="mt-12 grid md:grid-cols-2 gap-8">
              {[
                { key: "saligrama-vishwambhara", title: d.sal1, text: d.sal1Text },
                { key: "saligrama-damodara", title: d.sal2, text: d.sal2Text },
              ].map((s) => (
                <article key={s.key} data-testid={s.key} className="card-soft reveal p-8 text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-[#F4EBDB] flex items-center justify-center text-[#7B1818]">
                    <Gem size={22} />
                  </div>
                  <h3 className="mt-5 font-display text-2xl text-[#7B1818]">{s.title}</h3>
                  <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-[1.8]">{s.text}</p>
                </article>
              ))}
            </div>
          </div>

          <div>
            <SectionTitle icon={Landmark} eyebrow={d.s3Eyebrow} title={d.s3Title} />
            <div className="mt-12 grid md:grid-cols-2 gap-8">
              {[
                { key: "idol-kadagolu-krishna", title: d.o1, text: d.o1Text },
                { key: "idol-mukhyaprana", title: d.o2, text: d.o2Text },
              ].map((s) => (
                <article key={s.key} data-testid={s.key} className="card-soft reveal p-8">
                  <h3 className="font-display text-2xl text-[#7B1818]">{s.title}</h3>
                  <p className="mt-3 text-[14.5px] text-[#5C4D42] leading-[1.8]">{s.text}</p>
                </article>
              ))}
            </div>
          </div>

          <div>
            <SectionTitle icon={Crown} eyebrow={d.s4Eyebrow} title={d.s4Title} />
            <ol data-testid="idol-lineage-list" className="mt-12 max-w-2xl mx-auto space-y-0">
              {d.lineage.map((l, i) => (
                <li key={l.name} data-testid={`idol-lineage-step-${i}`} className="reveal relative flex gap-5 pb-8 last:pb-0">
                  {i < d.lineage.length - 1 && (
                    <span className="absolute left-[21px] top-11 bottom-0 w-px bg-gradient-to-b from-[#D4AF37]/70 to-[#D4AF37]/20" />
                  )}
                  <span className="relative z-10 w-11 h-11 shrink-0 rounded-full bg-[#F4EBDB] ring-1 ring-[#D4AF37]/50 flex items-center justify-center font-display text-[#7B1818] font-semibold">
                    {i + 1}
                  </span>
                  <div className="pt-1.5">
                    <div className="font-display text-xl text-[#2A2118] leading-tight">{l.name}</div>
                    <p className="mt-1 text-[13.5px] text-[#5C4D42]">{l.note}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="reveal text-center">
            <Link to="/parampara" data-testid="idols-view-parampara-btn" className="btn-primary">
              {d.cta} <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
